import { api, ensureApiAuthenticated } from '../services/api';

export interface ParseJobQueuedResponse {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | string;
  createdAt: string;
}

export interface ParseJobStatusResponse {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | string;
  result?: Record<string, unknown> | null;
  errorMessage?: string | null;
  createdAt?: string;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface ParseReportSummary {
  total_cards?: number;
  matched_count?: number;
  failed_count?: number;
  total_value?: number;
  offer_min?: number;
  offer_max?: number;
  [key: string]: unknown;
}

export interface ParseMatchedItem {
  id?: string;
  card_name?: string;
  name?: string;
  product_name?: string;
  set?: string;
  set_name?: string;
  card_number?: string;
  condition?: string;
  finish?: string;
  quantity?: number;
  price?: number;
  total_value?: number;
  tcgplayer_market?: number | null;
  manapool_market?: number | null;
  ck_buylist?: number | null;
  /** Fresh TCG Seller Portal Custom Export condition market (or null) */
  tcg_export_market?: number | null;
  /** Fresh Manapool variant low for this condition×finish (or null) */
  manapool_low?: number | null;
  tcg_low?: number | null;
  policy_price?: number | null;
  policy_price_debug?: {
    tcg_market?: number | null;
    tcg_low?: number | null;
    manapool_market?: number | null;
    manapool_low?: number | null;
    high?: number | null;
    low?: number | null;
    blend?: string | null;
    age_days?: number | null;
    policy_price?: number | null;
  } | null;
  /** Accumulated Manapool marketplace comps for charts */
  manapool_recent_sales?: Array<{
    sold_at?: string | null;
    price_cents: number;
    quantity?: number;
  }> | null;
  calculated_offer?: number | null;
  /** True when CK buylist raised the offer above the tier rate */
  offer_ck_floored?: boolean | null;
  /** Sellable on-hand qty for this printing×condition */
  on_hand_qty?: number | null;
  /** True when on_hand_qty >= buy overstock threshold */
  overstock?: boolean | null;
  /** Condition market resolution: tcg_export | manapool_low | mtgjson */
  price_source?: string | null;
  image_url?: string | null;
  reverse_image_url?: string | null;
  is_dual_faced?: boolean;
  reverse_scryfall_id?: string | null;
  scryfall_id?: string | null;
  warnings?: string[];
}

export interface ParseFailedItem {
  card_name?: string;
  name?: string;
  set?: string;
  card_number?: string;
  reason?: string;
  quantity?: number;
}

export interface ParseReportResponse {
  jobId: string;
  status: string;
  summary: ParseReportSummary;
  matched_items: ParseMatchedItem[];
  failed_items: ParseFailedItem[];
  csv_export_url?: string;
  html_report_url?: string;
}

export interface ParseImportResponse {
  copies_created: number;
  total_quantity: number;
  message?: string;
}

export interface ParseJobListItem {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | string;
  inputFileName?: string | null;
  inputFileSize?: number | null;
  errorMessage?: string | null;
  createdAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  summary?: ParseReportSummary;
}

export async function listParseJobs(params?: {
  hours?: number;
  limit?: number;
}): Promise<ParseJobListItem[]> {
  await ensureApiAuthenticated();

  const response = await api.get<ParseJobListItem[]>('/parse', {
    params: {
      hours: params?.hours ?? 72,
      limit: params?.limit ?? 50,
    },
  });

  return response.data;
}

export async function createParseJob(file: File): Promise<ParseJobQueuedResponse> {
  await ensureApiAuthenticated();

  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<ParseJobQueuedResponse>('/parse', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

export async function getParseJob(jobId: string): Promise<ParseJobStatusResponse> {
  await ensureApiAuthenticated();

  const response = await api.get<ParseJobStatusResponse>(`/parse/${jobId}`);
  return response.data;
}

export async function getParseReport(jobId: string): Promise<ParseReportResponse> {
  await ensureApiAuthenticated();

  const response = await api.get<ParseReportResponse>(`/parse/${jobId}/report`);
  return response.data;
}

export async function importParseToInventory(jobId: string): Promise<ParseImportResponse> {
  await ensureApiAuthenticated();

  const response = await api.post<ParseImportResponse>(`/parse/${jobId}/import`);
  return response.data;
}

export async function downloadParseMatchedCsv(jobId: string): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>(`/parse/${jobId}/matched.csv`, {
    responseType: 'blob',
  });

  const blobUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = `parse-${jobId}-matched.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
}

export async function downloadParseHtmlReport(jobId: string): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>(`/parse/${jobId}/report.html`, {
    responseType: 'blob',
  });

  const blobUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = `parse-${jobId}-report.html`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Headerless binder paste: `qty,"name [SET] number finish condition $price"`.
 * Two columns, so the 3-column TSV/CSV check does not see it.
 */
const HEADERLESS_QTY_BLOB_LINE =
  /^\s*\d+\s*[,;\t|]\s*(?:"[^"]*\[[^\]]+\]\s+\S*\d[^"]*"|[^,;\t|"]*\[[^\]]+\]\s+\S*\d)/;

/** Detect pasted TSV/CSV so we upload as .csv (columnar path) instead of freeform .txt. */
export function looksLikeColumnarCardList(text: string): boolean {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith('#'));
  if (lines.length === 0) return false;

  const sample = lines.slice(0, 5);
  if (sample.some((line) => HEADERLESS_QTY_BLOB_LINE.test(line))) {
    return true;
  }

  const delimiters = ['\t', ',', ';', '|'] as const;
  for (const delimiter of delimiters) {
    const columnarLines = sample.filter((line) => line.split(delimiter).length >= 3);
    if (columnarLines.length < Math.min(2, sample.length)) continue;

    const header = columnarLines[0].toLowerCase();
    const headerHints = ['name', 'card', 'set', 'edition', 'count', 'qty', 'quantity', 'collector'];
    if (headerHints.some((hint) => header.includes(hint))) {
      return true;
    }
    // No header but consistently delimited rows still belong on the CSV path.
    if (columnarLines.length >= 2) {
      return true;
    }
  }
  return false;
}
